import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createDashboard,
  getDashboardById,
  publishDashboard,
  updateDashboard,
} from '../../../../../services/dashboardBuilder';
import { DASHBOARD_STATES } from '../../constants';
import { DASHBOARD_EDITOR_COPY } from '../../constants/dashboardBuilderCopy';

const DEFAULT_WIDGET_W = 4;
const DEFAULT_WIDGET_H = 5;

const INITIAL_EDITOR_STATE = {
  id: null,
  title: '',
  description: '',
  state: DASHBOARD_STATES.DRAFT,
  sectionId: null,
  sectionPath: '',
  widgets: [],
  sharedWith: [],
  loading: false,
  saving: false,
  error: null,
  isDirty: false,
};

export const useDashboardEditor = () => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const loadRequestIdRef = useRef(0);
  const editorStateRef = useRef(INITIAL_EDITOR_STATE);

  const [editorState, setEditorState] = useState(INITIAL_EDITOR_STATE);

  const isEditing = dashboardId && dashboardId !== 'new';

  editorStateRef.current = editorState;

  const updateEditor = useCallback((updates) => {
    setEditorState((prev) => ({
      ...prev,
      ...updates,
      isDirty: true,
    }));
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setEditorState(INITIAL_EDITOR_STATE);
      return;
    }

    const requestId = ++loadRequestIdRef.current;
    const loadDashboard = async () => {
      setEditorState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await getDashboardById(dashboardId);
        const body = response?.body || {};

        if (requestId !== loadRequestIdRef.current) return;

        setEditorState({
          id: body.id || dashboardId,
          title: body.title || '',
          description: body.description || '',
          state: body.state || DASHBOARD_STATES.DRAFT,
          sectionId: body.sectionId || null,
          sectionPath: body.sectionPath || '',
          widgets: Array.isArray(body.widgets) ? body.widgets : [],
          sharedWith: Array.isArray(body.sharedWith) ? body.sharedWith : [],
          loading: false,
          saving: false,
          error: null,
          isDirty: false,
        });
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) return;
        setEditorState((prev) => ({
          ...prev,
          loading: false,
          error,
        }));
      }
    };

    loadDashboard();
  }, [dashboardId, isEditing]);

  const saveDraft = useCallback(async () => {
    setEditorState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const currentState = editorStateRef.current;
      let redirectToId = null;
      const payload = {
        title: currentState.title || 'Untitled Dashboard',
        description: currentState.description || '',
        state: DASHBOARD_STATES.DRAFT,
        sectionId: currentState.sectionId,
        sectionPath: currentState.sectionPath,
        widgets: currentState.widgets,
        sharedWith: currentState.sharedWith,
      };

      // TODO: Remove after backend integration; this is temporary to verify widgets are persisted in draft payloads.
      console.log('[DashboardEditor] saveDraft payload:', {
        title: payload.title,
        widgetCount: payload.widgets.length,
        widgets: payload.widgets,
        sharedWith: payload.sharedWith,
        sectionId: payload.sectionId,
        state: payload.state,
      });

      if (!isEditing) {
        const response = await createDashboard(payload);
        const created = response?.body || {};
        const createdId = created.id;

        setEditorState((prev) => ({
          ...prev,
          id: createdId || prev.id,
          state: DASHBOARD_STATES.DRAFT,
          saving: false,
          isDirty: false,
        }));

        if (createdId) {
          redirectToId = createdId;
          navigate(`/dashboards/${createdId}`, {
            replace: true,
            state: {
              toast: {
                severity: 'success',
                title: DASHBOARD_EDITOR_COPY.toast.draftSaved,
                description: DASHBOARD_EDITOR_COPY.toast.draftSavedDescription,
              },
            },
          });
        }
      } else {
        const idToSave = currentState.id || dashboardId;
        await updateDashboard(idToSave, payload);
        setEditorState((prev) => ({
          ...prev,
          state: DASHBOARD_STATES.DRAFT,
          saving: false,
          isDirty: false,
        }));
        if (idToSave) {
          redirectToId = idToSave;
          navigate(`/dashboards/${idToSave}`, {
            state: {
              toast: {
                severity: 'success',
                title: DASHBOARD_EDITOR_COPY.toast.draftSaved,
                description: DASHBOARD_EDITOR_COPY.toast.draftSavedDescription,
              },
            },
          });
        }
      }

      return { success: true, redirected: Boolean(redirectToId) };
    } catch (error) {
      setEditorState((prev) => ({ ...prev, saving: false, error }));
      return { success: false, error };
    }
  }, [dashboardId, isEditing, navigate]);

  const publish = useCallback(async (overrides = {}) => {
    const currentState = { ...editorStateRef.current, ...overrides };
    const validationErrors = [];

    if (!String(currentState.title || '').trim()) {
      validationErrors.push('title');
    }
    if (!currentState.sectionId) {
      validationErrors.push('sectionId');
    }
    if (validationErrors.length > 0) {
      return { success: false, validationErrors };
    }

    if (!currentState.sharedWith.length) {
      return { success: false, needsShare: true };
    }

    setEditorState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const payload = {
        title: String(currentState.title || '').trim() || 'Untitled Dashboard',
        description: currentState.description || '',
        sectionId: currentState.sectionId || null,
        sectionPath: currentState.sectionPath || '',
        widgets: Array.isArray(currentState.widgets) ? currentState.widgets : [],
        sharedWith: Array.isArray(currentState.sharedWith) ? currentState.sharedWith : [],
      };
      let idToPublish = currentState.id || dashboardId;

      if (!idToPublish) {
        const createResponse = await createDashboard({
          ...payload,
          state: DASHBOARD_STATES.DRAFT,
        });
        idToPublish = createResponse?.body?.id || null;
      } else {
        await updateDashboard(idToPublish, payload);
      }

      if (!idToPublish) {
        throw new Error('Unable to resolve dashboard id for publish');
      }

      await publishDashboard(idToPublish);
      setEditorState((prev) => ({
        ...prev,
        id: idToPublish,
        state: DASHBOARD_STATES.PUBLISHED,
        saving: false,
        isDirty: false,
      }));
      navigate(`/dashboards/${idToPublish}`, {
        state: {
          toast: {
            severity: 'success',
            title: DASHBOARD_EDITOR_COPY.toast.published,
            description: DASHBOARD_EDITOR_COPY.toast.publishedDescription,
          },
        },
      });
      return { success: true };
    } catch (error) {
      setEditorState((prev) => ({ ...prev, saving: false, error }));
      return { success: false, error };
    }
  }, [dashboardId, navigate]);

  const archive = useCallback(async () => {
    setEditorState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const currentState = editorStateRef.current;
      const idToArchive = currentState.id || dashboardId;
      await updateDashboard(idToArchive, { state: 'archived' });
      setEditorState((prev) => ({
        ...prev,
        state: 'archived',
        saving: false,
        isDirty: false,
      }));
      return { success: true };
    } catch (error) {
      setEditorState((prev) => ({ ...prev, saving: false, error }));
      return { success: false, error };
    }
  }, [dashboardId]);

  const addWidgets = useCallback((newWidgets) => {
    if (!Array.isArray(newWidgets) || !newWidgets.length) return;
    setEditorState((prev) => {
      const baseLength = prev.widgets.length;
      const widgetInstances = newWidgets.map((widget, index) => {
        const instanceId =
          widget.instanceId || `${Date.now()}_${widget.type || 'widget'}_${baseLength + index}`;
        const col = (baseLength + index) * DEFAULT_WIDGET_W;
        return {
          ...widget,
          instanceId,
          layout: widget.layout || {
            i: instanceId,
            x: col % 12,
            y: Math.floor(col / 12) * DEFAULT_WIDGET_H,
            w: DEFAULT_WIDGET_W,
            h: DEFAULT_WIDGET_H,
          },
        };
      });

      return {
        ...prev,
        widgets: [...prev.widgets, ...widgetInstances],
        isDirty: true,
      };
    });
  }, []);

  const removeWidget = useCallback((widgetInstanceId) => {
    setEditorState((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((widget) => widget.instanceId !== widgetInstanceId),
      isDirty: true,
    }));
  }, []);

  const toggleWidgetLock = useCallback((widgetInstanceId) => {
    setEditorState((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) =>
        w.instanceId === widgetInstanceId ? { ...w, locked: !w.locked } : w,
      ),
      isDirty: true,
    }));
  }, []);

  const updateLayouts = useCallback((_currentLayout, allLayouts) => {
    if (!allLayouts) return;
    const lgLayout = allLayouts.lg || [];
    setEditorState((prev) => {
      const updatedWidgets = prev.widgets.map((widget) => {
        const item = lgLayout.find((l) => l.i === widget.instanceId);
        if (!item) return widget;
        return {
          ...widget,
          layout: { i: item.i, x: item.x, y: item.y, w: item.w, h: item.h },
        };
      });
      return { ...prev, widgets: updatedWidgets, isDirty: true };
    });
  }, []);

  const canAddWidgets = useMemo(() => Boolean(editorState.sectionId), [editorState.sectionId]);

  return {
    ...editorState,
    isEditing,
    canAddWidgets,
    updateEditor,
    saveDraft,
    publish,
    archive,
    addWidgets,
    removeWidget,
    toggleWidgetLock,
    updateLayouts,
  };
};
